/**
 * Secure Logger utility for handling sensitive data in logs
 * Prevents exposure of user data, tokens, and other sensitive information in production
 */

class SecureLogger {
  /**
   * Generic logging method with environment-aware security
   * @param {string} level - Log level (log, warn, error, info)
   * @param {string} message - Log message
   * @param {any} data - Optional data to log (only in development)
   */
  static log(level, message, data = null) {
    if (process.env.NODE_ENV === 'development') {
      // Full logging in development
      if (data !== null) {
        console[level](message, data);
      } else {
        console[level](message);
      }
    } else if (level === 'error' || level === 'warn') {
      // Only sanitized errors/warnings in production
      const sanitizedMessage = this.sanitizeMessage(message);
      console[level](sanitizedMessage);
    }
    // No info/log messages in production to protect user privacy
  }

  /**
   * Development-only debug logging
   * @param {string} message - Debug message
   * @param {any} data - Debug data
   */
  static debug(message, data = null) {
    this.log('log', message, data);
  }

  /**
   * Safe info logging (sanitized in production)
   * @param {string} message - Info message
   * @param {any} data - Optional data
   */
  static info(message, data = null) {
    this.log('info', message, data);
  }

  /**
   * Warning logging (sanitized in production)
   * @param {string} message - Warning message
   * @param {any} data - Optional data
   */
  static warn(message, data = null) {
    this.log('warn', message, data);
  }

  /**
   * Error logging (sanitized in production)
   * @param {string} message - Error message
   * @param {any} error - Error object or data
   */
  static error(message, error = null) {
    this.log('error', message, error);
  }

  /**
   * Sanitize sensitive data from log messages
   * @param {string} message - Original message
   * @returns {string} - Sanitized message
   */
  static sanitizeMessage(message) {
    if (typeof message !== 'string') return message;
    
    return message
      // Remove UUIDs and user IDs
      .replace(/[a-f0-9-]{36}/gi, '[USER_ID]')
      .replace(/user_id:\s*[^,\s}]+/gi, 'user_id: [REDACTED]')
      .replace(/user:\s*[^,\s}]+/gi, 'user: [REDACTED]')
      // Remove email addresses
      .replace(/[\w.-]+@[\w.-]+\.\w+/gi, '[EMAIL]')
      // Remove tokens
      .replace(/bearer\s+[^\s]+/gi, 'bearer [TOKEN]')
      .replace(/token['":]?\s*[^\s,}'"]+/gi, 'token: [TOKEN]');
  }

  /**
   * Log user authentication status without exposing user ID
   * @param {string} action - What action is being performed
   * @param {object} session - Session object
   */
  static logUserAction(action, session) {
    const status = session?.user?.id ? 'authenticated' : 'not authenticated';
    this.debug(`🔍 ${action} for ${status} user`);
  }

  /**
   * Log API calls without exposing sensitive headers or data
   * @param {string} endpoint - API endpoint
   * @param {string} method - HTTP method
   * @param {boolean} hasAuth - Whether request includes authentication
   */
  static logApiCall(endpoint, method, hasAuth = false) {
    const sanitizedEndpoint = endpoint.replace(/\/[a-f0-9-]{36}/gi, '/[USER_ID]');
    this.debug(`🌐 API ${method} ${sanitizedEndpoint} (auth: ${hasAuth})`);
  }
}

export default SecureLogger;